import {
  Box,
  Container,
  FormControl,
  FormErrorMessage,
  Stack,
  VStack,
} from "@chakra-ui/react"
import FormInput from "@components/Forms/FormInput"
import H1 from "@components/Headings/H1"
import Layout from "@components/Layouts"
import BrandButton from "@components/Links/BrandButton"
import TextLink from "@components/Links/TextLink"
import { NextPage } from "next"
import { NextSeo } from "next-seo"
import { useState } from "react"
import { useForm } from "react-hook-form"

const ForgotPassword: NextPage = () => {
  const [formError, setFormError] = useState(false)
  const [formSubmitting, setFormSubmitting] = useState(false)
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false)

  // React Hook Form
  type FormData = {
    email: string
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  async function onSubmit(values: FormData) {
    try {
      setFormError(false)
      setForgotPasswordSuccess(false)
      setFormSubmitting(true)
      await fetch("/api/user/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      }).then((res) => {
        setFormSubmitting(false)
        if (res.ok) {
          setForgotPasswordSuccess(true)
          reset()
        } else {
          setFormError(true)
        }
      })
    } catch (error) {
      console.error(error)
      setFormSubmitting(false)
      setFormError(true)
    }
  }

  return (
    <Layout>
      <NextSeo title={"Forgot Password"} />
      <Container maxW={"container.sm"}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack spacing={8}>
            <H1>{"Forgot Your Password?"}</H1>

            <Stack
              justifyItems={"start"}
              w={"full"}
              flexDirection={{ base: "column", md: "row" }}
            >
              <Box>{"Enter your email address to reset your password."}</Box>
              <TextLink href={"/signin"}>{"Sign In"}</TextLink>
            </Stack>

            <FormControl isInvalid={!!errors.email}>
              <FormInput
                id={"email"}
                placeholder={"Email"}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value:
                      // eslint-disable-next-line no-control-regex
                      /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
                    message: "Email is not valid",
                  },
                })}
                type={"email"}
                autoComplete={"email"}
              />
              <FormErrorMessage>
                {errors.email && errors.email.message}
              </FormErrorMessage>
            </FormControl>

            <BrandButton
              type={"submit"}
              isLoading={formSubmitting || isSubmitting}
            >
              {"Submit"}
            </BrandButton>

            {formError && (
              <Box
                py={6}
                px={2}
                mb={8}
                color={"red.500"}
                borderColor={"red.500"}
                borderWidth={1}
                textAlign={"center"}
                w={"full"}
              >
                {"Error in submitting the form. Please try again."}
              </Box>
            )}

            {forgotPasswordSuccess && (
              <Box
                py={6}
                px={2}
                mb={8}
                color={"green.500"}
                borderColor={"green.500"}
                borderWidth={1}
                textAlign={"center"}
                w={"full"}
              >
                {
                  "We have sent you a reset password link to your email, please check your inbox."
                }
              </Box>
            )}
          </VStack>
        </form>
      </Container>
    </Layout>
  )
}

export default ForgotPassword
